import React, { useCallback } from 'react';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import { get, identity, isNil } from 'lodash-es';

import { getFormatter } from 'utils/formatter';
import { useIsMobile } from 'hooks/useIsMobile';

import { Column } from '../types';
import { BaseCell } from '../BaseCell';

import styles from './TableCell.module.scss';

export type TableCellProps<T, K extends keyof T = keyof T> = {
  data: T;
  column?: Column<T, K> | null;
  rows: number;
  rowLink?: (d: T, c: Column<T>) => string | undefined;
};

export function TableCell<T>({
  data,
  column,
  rowLink
}: TableCellProps<T>): React.ReactElement | null {
  const isMobile = useIsMobile();

  const Container = useCallback(
    ({ children }: { children?: React.ReactNode }) => {
      if (isNil(children)) return null;

      const to = column && rowLink?.(data, column);

      if (to && !column.noLink) {
        return <Link to={to}>{children}</Link>;
      }
      return <>{children}</>;
    },
    [rowLink, column, data]
  );

  if (column === null) return null;

  if (!column) {
    return (
      <BaseCell as="td">
        <Container />
      </BaseCell>
    );
  }
  const render = get(column, 'render', identity);
  const val = get(data, column.name) as T[keyof T];
  const formatter = getFormatter(data, getFormatter.Blank);
  return (
    <BaseCell
      as="td"
      data-name={column.name}
      rowSpan={column.lines && isMobile ? column.lines : undefined}
      className={cn(
        styles[`position-${column.position || 'left'}`],
        column.className
      )}
      {...(column.tdProps || {})}
    >
      <Container>{render(val, data, formatter)}</Container>
    </BaseCell>
  );
}
